import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import HelmetTags from "../../../MainComponents/HelmetTags";
import Loader from "../../../MainComponents/Loader.jsx";
import { useAuthState } from "../../../MainComponents/GlobalContext.jsx";
import StudentCoursesCard from "./components/StudentCoursesCard.jsx";

const PER_PAGE = 10;

const FILTERS = [
  { value: "all", label: "ALL", left: "left-0", top: "top-0" },
  {
    value: "calculus",
    label: "Calculus",
    left: "left-[14.285%]",
    top: "top-[14.285%]",
  },
  {
    value: "algebra",
    label: "Algebra",
    left: "left-[28.57%]",
    top: "top-[28.57%]",
  },
  {
    value: "statistics",
    label: "Statistics",
    left: "left-[42.855%]",
    top: "top-[42.855%]",
  },
  {
    value: "dynamics",
    label: "Dynamics",
    left: "left-[57.14%]",
    top: "top-[57.14%]",
  },
  {
    value: "pure",
    label: "Pure",
    left: "left-[71.425%]",
    top: "top-[71.425%]",
  },
  {
    value: "applied",
    label: "Applied",
    left: "left-[85.71%]",
    top: "top-[85.71%]",
  },
];

function StudentCourses({ teacher }) {
  const AuthState = useAuthState();
  const [left, setLeft] = useState("left-0");
  const [top, setTop] = useState("top-0");
  const [name, setName] = useState("ALL");
  const [branch, setBranch] = useState("all");
  const [coursesData, setCoursesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [toggleFilter, setToggleFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const token =
    AuthState?.userData?.student_token || AuthState?.userData?.admin_token;

  const fetchCourses = useCallback(
    async (pageNum, branchFilter, replace = false) => {
      if (!token) return;

      if (replace) {
        setLoading(true);
      } else {
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }

      try {
        const params = new URLSearchParams({
          page: String(pageNum),
          per_page: String(PER_PAGE),
        });
        if (branchFilter && branchFilter !== "all") {
          params.set("branch", branchFilter);
        }

        const res = await axios.get(
          `${process.env.REACT_APP_STUDENT_COURSES_API}?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const items = res.data?.data || [];
        const meta = res.data?.meta;
        const lastPage = Number(meta?.last_page || 1);
        const currentPage = Number(meta?.current_page || pageNum);

        setCoursesData((prev) => (replace ? items : [...prev, ...items]));
        setPage(currentPage);
        setHasMore(currentPage < lastPage);
      } catch (error) {
        if (replace) {
          setCoursesData([]);
          setHasMore(false);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
        loadingMoreRef.current = false;
      }
    },
    [token]
  );

  useEffect(() => {
    setCoursesData([]);
    setPage(1);
    setHasMore(true);
    fetchCourses(1, branch, true);
  }, [branch, fetchCourses]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loadingMoreRef.current) {
          fetchCourses(page + 1, branch, false);
        }
      },
      { rootMargin: "200px", threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, page, branch, fetchCourses, coursesData.length]);

  const handelSetFilter = (type, label, leftPos, topPos) => {
    setBranch(type);
    setName(label);
    if (leftPos) setLeft(leftPos);
    if (topPos) setTop(topPos);
  };

  return (
    <section className="student-courses-page h-auto width">
      <HelmetTags title={`Lectures | Mr.Mohamed Salama`} />

      <div
        className={`filters-container bg-light-gray mt-10 flex justify-center `}
      >
        <div
          className={`filters flex justify-between  items-center w-[840px] max-w-full h-12 relative md:flex-col  md:w-24  transition-all duration-200 ease-in-out ${
            toggleFilter ? "md:h-[448px] " : "md:h-16"
          }`}
          onClick={() => setToggleFilter(!toggleFilter)}
        >
          {FILTERS.map((filter, index) => (
            <div
              key={filter.value}
              onClick={() => {
                handelSetFilter(
                  filter.value,
                  filter.label,
                  filter.left,
                  filter.top
                );
              }}
              className={`w-[14.285%] flex justify-center items-center h-full cursor-pointer z-10 font-semibold text-sm md:h-16 md:w-24 transition-{height} duration-200 ease-in-out ${
                index === 0
                  ? "md:min-h-[64px]"
                  : toggleFilter
                    ? "md:opacity-100 md:h-16"
                    : "md:opacity-0 md:h-0 md:pointer-events-none"
              }`}
            >
              {filter.label}
              {index === 0 && (
                <FontAwesomeIcon
                  className={`hidden md:block ml-2 font-bold text-lg transition-all duration-200 ease-in-out ${
                    toggleFilter && "rotate-180"
                  }`}
                  icon={faChevronDown}
                />
              )}
            </div>
          ))}

          <div
            className={`selector-desktop md:hidden absolute w-[14.285%] h-full  ${left} md:left-0 bg-green duration-300 rounded-md md:h-16 md:w-24  top-0`}
          ></div>
          <div
            className={`selector-mobile hidden md:block   absolute w-[14.285%] h-full  left-0 bg-green duration-300 rounded-md md:h-16 md:w-24 ${top} `}
          ></div>
        </div>
      </div>

      <h1 className="course-name text-center text-3xl font-bold mt-12">
        {name} Lectures
      </h1>

      <div
        className={`student-courses-grid ${
          teacher && "sss:justify-items-start"
        }`}
      >
        {coursesData?.map((course) => (
          <StudentCoursesCard
            key={course.key}
            course={course}
            teacher={teacher}
          />
        ))}
        {!loading && coursesData?.length === 0 && (
          <p className="student-courses-empty">There are No Lectures Yet.</p>
        )}
      </div>

      {hasMore && !loading && (
        <div ref={sentinelRef} className="h-8 w-full" aria-hidden="true" />
      )}

      {(loading || loadingMore) && <Loader />}
    </section>
  );
}

export default StudentCourses;
